import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Shield, Smartphone, Copy, Check, AlertCircle, KeyRound, Globe, FileText, Upload, Trash2 } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

/**
 * Settings page — MFA Setup Guide + read-only view of IP whitelists.
 * Partners can enable/disable MFA here. IP whitelists are set by super
 * admin only; partners see what's configured but can't edit.
 */
export default function PanelSettings() {
    const [mfa, setMfa] = useState({ enabled: false, backup_codes_remaining: 0 });
    const [setupData, setSetupData] = useState(null);  // {secret,otpauth_uri,qr_data_url,backup_codes}
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);
    const [showDisable, setShowDisable] = useState(false);
    const [disablePw, setDisablePw] = useState("");
    const [whitelists, setWhitelists] = useState({ portal_ip_whitelist: [], api_ip_whitelist: [] });
    const [copied, setCopied] = useState("");

    const loadStatus = async () => {
        try {
            const [m, w] = await Promise.all([
                api.get("/auth/mfa/status"),
                api.get("/partner/ip-whitelist"),
            ]);
            setMfa(m.data);
            setWhitelists(w.data);
        } catch (e) { /* non-fatal */ }
    };
    useEffect(() => { loadStatus(); }, []);

    const startSetup = async () => {
        setBusy(true); setErr(""); setDone(false);
        try { const r = await api.post("/auth/mfa/setup"); setSetupData(r.data); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const verifyEnable = async () => {
        setBusy(true); setErr("");
        try {
            await api.post("/auth/mfa/enable", { code });
            setDone(true); setCode(""); setSetupData(null);
            await loadStatus();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const disable = async () => {
        setBusy(true); setErr("");
        try {
            await api.post("/auth/mfa/disable", disablePw ? { password: disablePw } : { code });
            setShowDisable(false); setDisablePw(""); setCode("");
            await loadStatus();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const copy = async (text, key) => {
        try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); } catch {}
    };

    return (
        <div data-testid="panel-settings" className="p-6 space-y-6 max-w-3xl">
            <div>
                <h1 className="text-xl font-bold">Security Settings</h1>
                <p className="text-xs text-slate-500 mt-1">Two-factor authentication & access controls for your account.</p>
            </div>

            {err && <div data-testid="settings-err" className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}

            {/* Receipt branding */}
            <ReceiptBranding />

            {/* MFA Section */}
            <section className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="font-bold text-base flex items-center gap-2">
                            <ShieldCheck className={`w-5 h-5 ${mfa.enabled ? "text-emerald-600" : "text-slate-400"}`} />
                            Two-factor authentication (2FA)
                        </h2>
                        <p className="text-xs text-slate-500 mt-1">
                            Adds a 6-digit code from your phone to logins and <strong>required</strong> for any payout above ₹10,000.
                        </p>
                    </div>
                    <span data-testid="mfa-status-badge"
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${mfa.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                        {mfa.enabled ? "Enabled" : "Not enabled"}
                    </span>
                </div>

                {done && (
                    <div data-testid="mfa-done" className="mt-4 bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-800 flex items-center gap-2">
                        <Check className="w-4 h-4" /> 2FA enabled! Keep your backup codes safe.
                    </div>
                )}

                {!mfa.enabled && !setupData && (
                    <div className="mt-4 space-y-3">
                        <SetupGuide />
                        <button data-testid="mfa-start" onClick={startSetup} disabled={busy}
                                className="btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2">
                            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                            <Shield className="w-4 h-4" /> Begin setup
                        </button>
                    </div>
                )}

                {setupData && (
                    <div className="mt-4 space-y-4" data-testid="mfa-setup-panel">
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                            <ol className="text-xs text-slate-700 space-y-2.5 list-decimal pl-4">
                                <li>Open Google Authenticator / Authy / 1Password on your phone.</li>
                                <li>Scan the QR code or paste the setup key.</li>
                                <li>Enter the 6-digit code that appears to confirm pairing.</li>
                            </ol>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                            <div className="flex flex-col items-center bg-white border border-slate-200 rounded-md p-4">
                                <img data-testid="mfa-qr" alt="MFA QR" src={setupData.qr_data_url} className="w-44 h-44" />
                                <div className="mt-3 text-[11px] text-slate-500">or copy this setup key</div>
                                <div className="mt-1 w-full flex items-center gap-1 bg-slate-50 border border-slate-200 rounded p-2">
                                    <code className="text-[11px] break-all flex-1">{setupData.secret}</code>
                                    <button onClick={() => copy(setupData.secret, "secret")} className="text-slate-500">
                                        {copied === "secret" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Backup codes (save these!)</label>
                                    <div className="mt-1 bg-amber-50 border border-amber-200 rounded p-3 grid grid-cols-2 gap-1 font-mono text-xs">
                                        {setupData.backup_codes.map((c) => <div key={c}>{c}</div>)}
                                    </div>
                                    <button onClick={() => copy(setupData.backup_codes.join("\n"), "codes")}
                                            className="mt-1 text-xs text-indigo-600 hover:underline flex items-center gap-1">
                                        {copied === "codes" ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy all backup codes</>}
                                    </button>
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Enter 6-digit code to activate</label>
                                    <input data-testid="mfa-code" value={code} inputMode="numeric" maxLength={6}
                                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                            placeholder="123 456"
                                            className="input w-full mt-1 font-mono text-base tracking-[0.3em] text-center" />
                                </div>
                                <button data-testid="mfa-enable" onClick={verifyEnable} disabled={busy || code.length !== 6}
                                        className="btn-primary w-full py-2 rounded-md text-sm font-semibold">
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Activate 2FA"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {mfa.enabled && !showDisable && (
                    <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
                        <div className="text-xs text-slate-600">
                            Backup codes remaining: <strong>{mfa.backup_codes_remaining}</strong>
                        </div>
                        <span data-testid="mfa-mandatory-note" className="text-xs text-slate-500">Mandatory for all accounts</span>
                    </div>
                )}

                {mfa.enabled && showDisable && (
                    <div data-testid="mfa-disable-panel" className="mt-4 bg-rose-50 border border-rose-200 rounded-md p-4 space-y-3">
                        <p className="text-xs text-rose-800">Confirm with your current 6-digit code OR your password.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input value={code} inputMode="numeric" maxLength={6} data-testid="mfa-disable-code"
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                    placeholder="123 456" className="input text-sm font-mono" />
                            <input type="password" value={disablePw} data-testid="mfa-disable-pw"
                                    onChange={(e) => setDisablePw(e.target.value)}
                                    placeholder="…or password" className="input text-sm" />
                        </div>
                        <div className="flex gap-2">
                            <button onClick={disable} disabled={busy || (!code && !disablePw)} data-testid="mfa-disable-confirm"
                                    className="px-4 py-2 rounded-md text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">
                                Disable 2FA
                            </button>
                            <button onClick={() => { setShowDisable(false); setDisablePw(""); setCode(""); }}
                                    className="px-4 py-2 rounded-md text-sm border border-slate-200 hover:bg-white">Cancel</button>
                        </div>
                    </div>
                )}
            </section>

            {/* IP Whitelists (read-only) */}
            <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="ip-whitelist-section">
                <h2 className="font-bold text-base flex items-center gap-2"><Globe className="w-5 h-5 text-slate-500" /> IP whitelist</h2>
                <p className="text-xs text-slate-500 mt-1">Managed by our ops team on request. Share your office/server IPs with us to restrict access.</p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <WhitelistBlock title="Portal login" icon={Smartphone}
                                      entries={whitelists.portal_ip_whitelist} testid="wl-portal" />
                    <WhitelistBlock title="API calls" icon={KeyRound}
                                      entries={whitelists.api_ip_whitelist} testid="wl-api" />
                </div>
            </section>
        </div>
    );
}

function SetupGuide() {
    return (
        <div data-testid="mfa-setup-guide" className="bg-indigo-50 border border-indigo-200 rounded-md p-4">
            <h3 className="font-semibold text-sm flex items-center gap-2 text-indigo-900">
                <AlertCircle className="w-4 h-4" /> Why enable 2FA?
            </h3>
            <ul className="mt-2 text-xs text-indigo-800 space-y-1.5 list-disc pl-5">
                <li>Payouts above ₹10,000 are blocked until 2FA is on (otherwise you'll see <code className="bg-white px-1 rounded">mfa_not_enrolled</code>).</li>
                <li>Even if your password leaks, nobody can sign in without your phone.</li>
                <li>Takes 60 seconds — grab Google Authenticator, Authy, or 1Password first.</li>
            </ul>
        </div>
    );
}

function WhitelistBlock({ title, entries, icon: Icon, testid }) {
    return (
        <div data-testid={testid} className="border border-slate-200 rounded-md p-3">
            <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <Icon className="w-4 h-4 text-slate-500" /> {title}
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${entries.length === 0 ? "bg-slate-100 text-slate-600" : "bg-indigo-100 text-indigo-800"}`}>
                    {entries.length === 0 ? "No restriction" : `${entries.length} entries`}
                </span>
            </div>
            {entries.length > 0 ? (
                <ul className="mt-2 space-y-1 font-mono text-xs text-slate-700">
                    {entries.map((e) => <li key={e} className="truncate">• {e}</li>)}
                </ul>
            ) : (
                <p className="text-[11px] text-slate-500 mt-2">Any IP can access. Contact support to restrict.</p>
            )}
        </div>
    );
}

function ReceiptBranding() {
    const [logo, setLogo] = useState("");
    const [footer, setFooter] = useState("");
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/partner/profile");
                setLogo(r.data?.partner?.receipt_logo || "");
                setFooter(r.data?.partner?.receipt_footer || "");
            } catch {}
        })();
    }, []);

    const pickFile = (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        if (f.size > 140 * 1024) { setErr("Logo must be ≤140 KB"); return; }
        const reader = new FileReader();
        reader.onload = () => setLogo(reader.result);
        reader.readAsDataURL(f);
    };

    const save = async () => {
        setBusy(true); setErr(""); setSaved(false);
        try {
            await api.patch("/partner/profile", { receipt_logo: logo || "", receipt_footer: footer });
            setSaved(true); setTimeout(() => setSaved(false), 2000);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <section data-testid="receipt-branding" className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <div>
                <h2 className="font-bold text-base flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-500" /> PayIn receipt branding
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                    Optional. Appears on every PDF receipt partners can download for successful captures.
                </p>
            </div>
            {err && <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Logo (PNG/JPG, ≤140 KB)</label>
                    <div className="mt-2 border border-dashed border-slate-300 rounded-md p-3 bg-slate-50">
                        {logo ? (
                            <div className="flex items-center gap-3">
                                <img src={logo} alt="logo preview" className="w-24 h-16 object-contain bg-white border border-slate-200 rounded p-1" data-testid="receipt-logo-preview" />
                                <button onClick={() => setLogo("")} data-testid="receipt-logo-clear"
                                            className="text-xs text-rose-600 hover:text-rose-800 flex items-center gap-1 font-semibold">
                                    <Trash2 className="w-3.5 h-3.5" /> Remove
                                </button>
                            </div>
                        ) : (
                            <label className="flex flex-col items-center justify-center cursor-pointer text-xs text-slate-500 py-3">
                                <Upload className="w-5 h-5 mb-1" />
                                <span>Click to upload</span>
                                <input data-testid="receipt-logo-input" type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={pickFile} />
                            </label>
                        )}
                    </div>
                </div>
                <div>
                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Footer line (optional)</label>
                    <textarea data-testid="receipt-footer" value={footer} onChange={(e) => setFooter(e.target.value)}
                                rows={4} maxLength={240} placeholder="e.g. Thank you for your payment. For queries, write to accounts@acme.in"
                                className="input w-full text-sm mt-2 resize-none" />
                    <p className="text-[11px] text-slate-400 mt-1">{footer.length}/240</p>
                </div>
            </div>
            <div className="flex justify-end items-center gap-3">
                {saved && <span className="text-xs text-emerald-700 font-semibold flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Saved</span>}
                <button data-testid="receipt-save" onClick={save} disabled={busy}
                        className="btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save branding
                </button>
            </div>
        </section>
    );
}
